import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { DollarOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Row, Select } from 'antd';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import WorkflowHandle from './WorkflowHandle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

import useToggle from '../../common/hooks/useToggle';

import { getType, updateData } from './workflowHelpers';

const getHoverText = (t) => `
Use this step to add a change order to the ${t('Project').toLowerCase()} contract.
`;

const getErrorDetails = ({ hasSovTableLink }) => ({
  content: hasSovTableLink ? (
    <div style={{ maxWidth: 300 }}>
      Select at least one preload-enabled <b>Schedule of Values Table</b>.
    </div>
  ) : (
    <div style={{ maxWidth: 300 }}>
      This step requires the following fields to be configured:
      <ul style={{ paddingLeft: 16, marginTop: 8 }}>
        <li><b>Section to Update</b></li>
        <li><b>Custom Section Name</b> (if Custom section is selected)</li>
        <li>
          The following <b>Field Mappings</b> (set via <b>Configure</b>):
          <ul style={{ paddingLeft: 16, marginTop: 4 }}>
            <li><b>Description</b></li>
            <li><b>Contract Amount</b></li>
            <li><b>Project</b></li>
          </ul>
        </li>
      </ul>
    </div>
  ),
});

const getDisabledText = (t) => `This step requires either at least one Text field and one ${t('Project')} Select field, or one Schedule of Values table with preloading enabled`;

const getChangeOrderFields = (t) => [
  { key: 'description', text: 'Description', type: 'text' },
  { key: 'contractAmount', text: 'Contract Amount', type: 'text' },
  { key: 'projectId', text: t('Project'), type: 'dropdown - Projects' },
  { key: 'subcontractId', text: 'Sub-Contract', type: 'dropdown - Sub-Contracts' },
];

const requiredFields = new Set(['description', 'contractAmount', 'projectId']);

const hasSovPreloadingEnabled = ({
  configProps: {
    preloadExistingEntries,
    preloadProject,
    preloadDropdown,
    preloadSovTemplateId,
    preloadSovSectionId,
  } = {},
} = {}) => (
  preloadExistingEntries
  && (preloadProject || preloadDropdown)
  && preloadSovTemplateId !== undefined
  && preloadSovSectionId
);

const getSovTableOptions = (sections = []) => (
  sections.flatMap(({ fields = [], name: sectionName }) => (
    fields
      .filter((field) => getType(field) === 'table - ScheduleOfValues')
      .filter(hasSovPreloadingEnabled)
      .map((field) => ({
        label: `${sectionName} - ${field.configProps?.title}`,
        value: field.id,
      }))
  ))
);

const sectionOptions = [
  {
    label: 'Changes',
    value: 'changes',
  },
  {
    label: 'Non Holdback',
    value: 'nonHoldback',
  },
  {
    label: 'Custom',
    value: 'custom',
  },
];

export default function WorkflowUpdateContractNode({
  setElements,
  setDataMap,
  isDisplay,
  name,
  sections = [],
  setDisableNext,
} = {}) {
  function UpdateContractNode({
    id,
    draggable,
    disabled,
    data = {},
  }) {
    const { t } = useTranslation();
    const HOVER_TEXT = getHoverText(t);
    const DISABLED_TEXT = getDisabledText(t);
    const changeOrderFields = getChangeOrderFields(t);
    const title = `Update Contract${disabled ? ' - DISABLED' : ''}`;

    const {
      section: initialSection = 'changes',
      customSection: initialCustomSection = '',
      fieldMappings: initialFieldMappings = {},
      hasSovTableLink: initialHasSovTableLink = false,
      sovTableFieldIds: initialSovTableFieldIds = [],
      autoSubmitInvoice: initialAutoSubmitInvoice = false,
    } = data;

    const [selectedSection, setSelectedSection] = useState(initialSection);
    const [customSection, setCustomSection] = useState(initialCustomSection);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [hasSovTableLink, setHasSovTableLink] = useState(initialHasSovTableLink);
    const [sovTableFieldIds, setSovTableFieldIds] = useState(initialSovTableFieldIds);
    const [autoSubmitInvoice, setAutoSubmitInvoice] = useState(initialAutoSubmitInvoice);

    const {
      isToggled: isMappingDrawerVisible,
      toggle: toggleMappingDrawer,
    } = useToggle();

    const setMap = {
      section: setSelectedSection,
      customSection: setCustomSection,
      fieldMappings: setFieldMappings,
      hasSovTableLink: setHasSovTableLink,
      sovTableFieldIds: setSovTableFieldIds,
      autoSubmitInvoice: setAutoSubmitInvoice,
    };

    const sovTableOptions = useMemo(() => getSovTableOptions(sections), [sections]);

    const hasPreloadEnabledSovTables = sovTableOptions.length > 0;

    const disableNext = useMemo(() => {
      if (hasSovTableLink) return !sovTableFieldIds?.length;
      return !selectedSection
        || (selectedSection === 'custom' && !customSection)
        || !fieldMappings?.description
        || !fieldMappings?.contractAmount
        || !fieldMappings?.projectId;
    }, [customSection, selectedSection, fieldMappings, hasSovTableLink, sovTableFieldIds]);

    useEffect(() => {
      if (!setDisableNext) return;

      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [disableNext, setDisableNext, id]);

    const onDataChange = useCallback((key) => (newValue) => {
      if (!setDataMap || !id) return;
      setMap[key]?.(newValue);
      setDataMap(updateData(id, { [key]: newValue }));
    }, [id, setDataMap]);

    const onHasSovTableLinkChange = useCallback((checked) => {
      if (!setDataMap || !id) return;
      setHasSovTableLink(checked);
      const update = { hasSovTableLink: checked };
      if (checked) {
        setFieldMappings({});
        update.fieldMappings = {};
      } else {
        setSovTableFieldIds([]);
        setAutoSubmitInvoice(false);
        update.sovTableFieldIds = [];
        update.autoSubmitInvoice = false;
      }
      setDataMap(updateData(id, update));
    }, [id, setDataMap]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        Icon={DollarOutlined}
        type="updateContract"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={disableNext ? getErrorDetails({ hasSovTableLink }) : undefined}
      >
        {!draggable && (
          <div>
            <OnTraccrCheckbox
              style={{ marginTop: 10 }}
              value={hasSovTableLink}
              onChange={onHasSovTableLinkChange}
              disabled={!hasPreloadEnabledSovTables}
              label="Link SOV table(s)"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Enable SOV preloading in a Schedule of Values table field before linking
                  it to this workflow step.
                  Linked tables update the preloaded project SOV rows when the form is submitted.
                </div>
              )}
            />
            {hasSovTableLink ? (
              <>
                <Row style={{ marginTop: 10 }}>
                  Select SOV Table Field(s):
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    mode="multiple"
                    onChange={onDataChange('sovTableFieldIds')}
                    value={sovTableFieldIds}
                    showSearch
                    optionFilterProp="label"
                    options={sovTableOptions}
                  />
                </Row>
                <OnTraccrCheckbox
                  style={{ marginTop: 10 }}
                  value={autoSubmitInvoice}
                  onChange={onDataChange('autoSubmitInvoice')}
                  label="Auto Submit Invoice on SOV Page"
                  hoverNode={(
                    <div style={{ width: 300 }}>
                      When the form is submitted, automatically submit the updated schedule of values.
                    </div>
                  )}
                />
              </>
            ) : (
              <>
                <Row style={{ marginTop: 10 }}>
                  Section to Update:
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    onChange={onDataChange('section')}
                    value={selectedSection}
                    optionFilterProp="label"
                    options={sectionOptions}
                  />
                </Row>
                {selectedSection === 'custom' && (
                  <>
                    <Row style={{ marginTop: 10 }}>
                      Custom Section Name:
                    </Row>
                    <Row style={{ margin: '10px 0px' }}>
                      <OnTraccrTextInput
                        onChange={(e) => {
                          const {
                            target: {
                              value: newValue,
                            } = {},
                          } = e;
                          onDataChange('customSection')(newValue);
                        }}
                        value={customSection}
                        disabled={selectedSection !== 'custom'}
                      />
                    </Row>
                  </>
                )}
                <Row style={{ marginTop: 10 }}>
                  <BorderlessButton
                    title="Configure"
                    style={{ margin: 5 }}
                    iconNode={<SettingOutlined />}
                    onClick={toggleMappingDrawer}
                  />
                </Row>
                <WorkflowSimpleMappingConfigureDrawer
                  id={id}
                  visible={isMappingDrawerVisible}
                  onClose={toggleMappingDrawer}
                  onSubmit={toggleMappingDrawer}
                  sourceName={name}
                  sourceSections={sections}
                  fieldMappings={fieldMappings}
                  onFieldMappingsChange={onDataChange('fieldMappings')}
                  data={data}
                  setDataMap={setDataMap}
                  fields={changeOrderFields}
                  requiredFields={requiredFields}
                />
              </>
            )}
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  }

  UpdateContractNode.propTypes = {
    id: PropTypes.string.isRequired,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    data: PropTypes.shape({
      section: PropTypes.string,
      customSection: PropTypes.string,
      fieldMappings: PropTypes.shape({}),
      hasSovTableLink: PropTypes.bool,
      sovTableFieldIds: PropTypes.arrayOf(PropTypes.string),
      autoSubmitInvoice: PropTypes.bool,
    }),
  };

  UpdateContractNode.defaultProps = {
    draggable: false,
    disabled: false,
    data: {},
  };

  return UpdateContractNode;
}
